import React, { useEffect, useState } from "react";
import axios from "axios";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { maskAccount } from "../../../utils/bankMask";
import { padCategoryLabel } from "../../../utils/orderAccountKind";

/**
 * PAD history of one order (corporate Business PAD contract, 2026-10-25).
 *
 * GET admin/investments/:id/pad-authorizations answers every PAD row the order
 * ever had. A re-authorization inserts a new row and supersedes the old one
 * (I2: a PAD binds to one instrument version), and a Business PAD exists only
 * from a completed pad_business DocuSign envelope (I3), so the history is the
 * record of who consented to which account, when. Shown as the API sent it,
 * oldest first as the API orders it; the account is the last 4 only (I9), and
 * nothing outside the contract's fields is rendered.
 *
 * I1: a row's account suffix and payor show only when the order's resolved
 * kind is known and the row's category belongs to it (ENTITY with BUSINESS,
 * INDIVIDUAL with PERSONAL). Otherwise the row is still listed (status,
 * source, dates) with its coordinates hidden and a short reason.
 */

const SOURCE_LABELS = { PORTAL: "Portal", DOCUSIGN: "DocuSign" };

const KIND_CATEGORY = { ENTITY: "BUSINESS", INDIVIDUAL: "PERSONAL" };

/** Why this row's account and payor are hidden for an order of this kind, or null. */
function hiddenReason(kind, row) {
  const category = KIND_CATEGORY[kind];
  if (!category) return "Hidden: the order's account kind is not resolved";
  if (row.padCategory !== category) return "Hidden: this PAD's category does not match the order's account kind";
  return null;
}

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const when = (v) => (v ? formatAuditTimestamp(v) : "-");

function signerText(row) {
  const who = [row.signerName, row.signerTitle].filter(Boolean).join(", ");
  return who || "-";
}

/** The response body as a list: { data: [...] } or a bare array. */
function historyOf(body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

const PadHistoryPanel = ({ investmentId, kind }) => {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!investmentId) return undefined;
    let live = true;
    setState("loading");
    axios
      .get(`${process.env.REACT_APP_API}admin/investments/${investmentId}/pad-authorizations`, {
        headers: { "x-access-token": localStorage.getItem("accessToken") },
      })
      .then((res) => {
        if (!live) return;
        setRows(historyOf(res?.data));
        setState("ready");
      })
      .catch((err) => {
        if (!live) return;
        setRows([]);
        setState(err?.response?.status === 403 ? "refused" : "failed");
      });
    return () => {
      live = false;
    };
  }, [investmentId]);

  let body;
  if (state === "refused") {
    body = <p className="muted">The PAD history is not visible to your role.</p>;
  } else if (state === "failed") {
    body = <p className="muted">The PAD history could not be loaded.</p>;
  } else if (state === "loading") {
    body = <p className="muted">Loading the PAD history.</p>;
  } else if (rows.length === 0) {
    body = <p className="muted">No PAD authorization is on file for this order.</p>;
  } else {
    body = (
      <div style={{ overflowX: "auto" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>PAD #</th>
              <th>Status</th>
              <th>Category</th>
              <th>Source</th>
              <th>Envelope</th>
              <th>Signed by</th>
              <th>Signer role</th>
              <th>Accepted</th>
              <th>Account</th>
              <th>Payor</th>
              <th>Amount</th>
              <th>Agreement</th>
              <th>Instrument</th>
              <th>Superseded</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const hidden = hiddenReason(kind, row);
              return (
                <tr key={row.id} data-pad-status={row.status}>
                  <td className="mono">#{row.id}</td>
                  <td>{dash(row.status)}</td>
                  <td>{padCategoryLabel(row.padCategory)}</td>
                  <td>{SOURCE_LABELS[row.source] || dash(row.source)}</td>
                  <td className="mono">{dash(row.envelopeId)}</td>
                  <td>{signerText(row)}</td>
                  <td>{dash(row.signerRole)}</td>
                  <td>{when(row.acceptedAt)}</td>
                  {hidden ? (
                    <td className="muted" data-coordinates-hidden>{hidden}</td>
                  ) : (
                    <td className="mono">{maskAccount(row.accountLast4) || "-"}</td>
                  )}
                  <td>{hidden ? "Hidden" : dash(row.payorName)}</td>
                  <td>{row.amount ? `${row.amount} ${row.currency || ""}`.trim() : "-"}</td>
                  <td>{dash(row.agreementVersion)}</td>
                  <td className="mono">{row.instrumentId ? `#${row.instrumentId}` : "-"}</td>
                  <td>{row.supersededById ? `by #${row.supersededById}` : "-"}</td>
                  <td>{when(row.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section className="pad-history" aria-labelledby="pad-history-title">
      <h2 id="pad-history-title" className="title" style={{ fontSize: "16px", color: "#333" }}>
        PAD authorizations
      </h2>
      {body}
    </section>
  );
};

export default PadHistoryPanel;
